import { useEffect, useState } from "react";
import { COLORS, Ic, ICONS, Mark } from "@/lib/theme";
import { isPhoneApp } from "@/lib/phoneApp";
import { apiGet, apiPdf, formatApiError } from "@/lib/api";

const STATIC_BODIES = {
  welcome: {
    title: "Welcome to LinkShul",
    paragraphs: [
      "We are excited to officially welcome you to the LinkShul family. Welcome to our united, empowered and innovative community.",
      "We do our utmost to create an accepting and non-judgmental environment, offering various portals of entry into Judaism and Jewish communal life.",
      "At the heart of our community sits the historic Linksfield-Senderwood Hebrew Community Synagogue. Dynamic weekly and holiday services are provided by our Rabbinic Couple and various committees, for all ages of our members.",
    ],
  },
  credits: {
    title: "Credits",
    paragraphs: [
      "**LinkShul**\nThe LinkShul community app, Johannesburg.",
      "**Rabbinic leadership**\nRabbi Levi Avtzon · Chaya Avtzon",
      "**Community**\nWith gratitude to the LinkShul office, committees, and members who make our community what it is.",
      "**Website**\nwww.linkshul.co.za",
      "© LinkShul. All Rights Reserved.\nPowered by Farrell Berson",
    ],
  },
  history: {
    title: "History of LinkShul",
    paragraphs: [
      "The Linksfield-Senderwood Hebrew Congregation has served the eastern suburbs of Johannesburg for decades — a warm, dynamic community centred around Torah, tefillah, and chesed.",
      "Over the years, LinkShul has grown to include the Saltzman Family Community Centre, Mikvah Rachel, and the LinksYouth Shul & Centre — each serving a vital role in the life of our community.",
      "For the full history, please visit www.linkshul.co.za.",
    ],
  },
  security: {
    title: "Security",
    paragraphs: [
      "The safety and security of our community is of paramount importance.",
      "LinkShul works closely with security professionals to ensure a safe environment during services, events, and daily activities.",
      "If you notice anything of concern, please contact the office immediately on 011 640 5812.",
    ],
  },
  team: {
    title: "Our Team",
    paragraphs: [
      "**Rabbinic Couple**\nRabbi Levi Avtzon and Chaya Avtzon lead our community with warmth, wisdom, and openness. Please contact the office for further information.",
      "**Office**\nOur dedicated office team runs day-to-day operations and supports members with membership, events and lifecycle needs.",
      "**Committees**\nA range of committees — Chesed, Chai Club, Youth, Learning, Security — power the community's activities.",
    ],
  },
  donations: {
    title: "My Donations",
    _loader: "donations",
  },
};

export default function Detail({ detail, onClose, onNav }) {
  const [events, setEvents] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [learning, setLearning] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [donations, setDonations] = useState([]);
  const [pdf, setPdf] = useState(null);
  const [pdfError, setPdfError] = useState("");
  const [contact, setContact] = useState(null);

  useEffect(() => {
    if (!detail) return;
    const b = detail.body;
    if (b === "events") apiGet("/events").then((d) => setEvents((d.events || []).filter((event) => !isPhoneApp || Date.parse(event.date_iso) >= Date.now()))).catch(() => {});
    if (b === "newsletters") apiGet(isPhoneApp ? "/phone-newsletters" : "/newsletters").then((d) => setNewsletters(d.newsletters || [])).catch(() => {});
    if (b === "learning") apiGet("/learning").then((d) => setLearning(d.learning || [])).catch(() => {});
    if (b === "announcements") apiGet("/announcements").then((d) => setAnnouncements(d.announcements || [])).catch(() => {});
    if (b === "directory" && !isPhoneApp) apiGet("/directory").then((d) => setDirectory(d.members || [])).catch(() => {});
    if (b === "donations") apiGet("/donations/history").then((d) => setDonations(d.donations || [])).catch(() => {});
    if (b === "contact" || (isPhoneApp && ["directory", "team"].includes(b))) apiGet("/contact").then(setContact).catch(() => {});
  }, [detail]);

  useEffect(() => () => { if (pdf) URL.revokeObjectURL(pdf); }, [pdf]);

  const openPdf = async (id) => {
    setPdfError("");
    try {
      const file = await apiPdf(`/phone-newsletters/${id}`);
      setPdf(URL.createObjectURL(file));
    } catch (error) {
      setPdfError(formatApiError(error));
    }
  };

  if (!detail) return null;
  const b = detail.body;
  const staticContent = STATIC_BODIES[b];

  return (
    <div data-testid="detail-overlay" className="anim-slideIn"
      style={{ position: "absolute", inset: 0, background: COLORS.cream, zIndex: 30, display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "18px 18px 12px", background: "#fff", borderBottom: "1px solid " + COLORS.border }}>
        <button data-testid="detail-close" onClick={onClose} aria-label="Back" className="no-tap-highlight" style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
          <Ic d={ICONS.back} color={COLORS.purple} size={26} />
        </button>
        <div className="font-heading" style={{ fontWeight: 800, fontSize: 19, color: COLORS.ink }}>{detail.title}</div>
      </div>

      <div className="ls-scroll" style={{ flex: 1, overflowY: "auto", padding: "16px 22px 30px" }}>
        {b === "events" && events.map((e) => (
          <div key={e.id} className="ls-card" style={{ padding: 16, marginBottom: 12 }}>
            <div style={{ fontWeight: 800, fontSize: 15 }}>{e.title}</div>
            <div style={{ color: COLORS.purple, fontWeight: 700, fontSize: 13, marginTop: 3 }}>{e.date_display}</div>
            <div style={{ color: COLORS.muted, fontWeight: 600, fontSize: 13 }}>{e.location}</div>
            {e.description && <div style={{ marginTop: 8, color: COLORS.ink, fontWeight: 600, fontSize: 13, lineHeight: 1.5 }}>{e.description}</div>}
          </div>
        ))}

        {b === "newsletters" && isPhoneApp && <>
          {pdf ? <><button className="ls-outline-btn" onClick={() => setPdf(null)}>Back to newsletters</button><iframe title="Newsletter PDF" src={pdf} style={{ width: "100%", height: "70vh", border: 0 }} /></> : newsletters.map((n) => <button key={n.id} className="ls-outline-btn" style={{ display: "block", marginBottom: 10 }} onClick={() => openPdf(n.id)}>{n.title}</button>)}
          {pdfError && <p role="alert">{pdfError}</p>}
          {!newsletters.length && <p>No newsletters have been uploaded yet.</p>}
        </>}
        {b === "newsletters" && !isPhoneApp && newsletters.map((n) => (
          <div key={n.id} className="ls-card" style={{ padding: 16, marginBottom: 12 }}>
            <div style={{ fontWeight: 800, fontSize: 15 }}>{n.title}</div>
            {n.published_at && <div style={{ color: COLORS.purple, fontWeight: 700, fontSize: 12, marginTop: 3 }}>{new Date(n.published_at).toLocaleDateString()}</div>}
            {n.excerpt && <div style={{ marginTop: 8, color: COLORS.muted, fontWeight: 600, fontSize: 13, lineHeight: 1.5, fontStyle: "italic" }}>{n.excerpt}</div>}
            {n.body && <div style={{ marginTop: 10, color: COLORS.ink, fontWeight: 600, fontSize: 13.5, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{n.body}</div>}
          </div>
        ))}

        {b === "learning" && learning.map((l) => (
          <div key={l.id} className="ls-card" style={{ padding: 16, marginBottom: 12 }}>
            <div style={{ fontWeight: 800, fontSize: 15 }}>{l.title}</div>
            <div style={{ color: COLORS.purple, fontWeight: 700, fontSize: 13, marginTop: 3 }}>{l.when}</div>
            <div style={{ color: COLORS.muted, fontWeight: 600, fontSize: 12.5 }}>{l.location}{l.teacher ? " · " + l.teacher : ""}</div>
            {l.description && <div style={{ marginTop: 8, color: COLORS.ink, fontWeight: 600, fontSize: 13, lineHeight: 1.5 }}>{l.description}</div>}
          </div>
        ))}

        {b === "announcements" && announcements.map((a) => (
          <div key={a.id} className="ls-card" style={{ padding: 16, marginBottom: 12, borderLeft: a.priority === "high" ? `4px solid ${COLORS.gold}` : `4px solid ${COLORS.purple}` }}>
            <div style={{ fontWeight: 800, fontSize: 15 }}>{a.title}</div>
            {a.posted_at && <div style={{ color: COLORS.muted, fontWeight: 700, fontSize: 12, marginTop: 3 }}>{new Date(a.posted_at).toLocaleDateString()}</div>}
            <div style={{ marginTop: 8, color: COLORS.ink, fontWeight: 600, fontSize: 13.5, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{a.body}</div>
          </div>
        ))}

        {b === "directory" && !isPhoneApp && (
          <>
            <div style={{ color: COLORS.muted, fontWeight: 600, fontSize: 13, marginBottom: 12 }}>{directory.length} approved members</div>
            {directory.map((m) => (
              <div key={m.id} className="ls-card" style={{ padding: 12, marginBottom: 8, display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: COLORS.lilac, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Ic d={ICONS.user} color={COLORS.plum} size={20} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 14 }}>{m.first_name} {m.last_name}</div>
                  {m.email && <div style={{ fontSize: 12, color: COLORS.muted, fontWeight: 600 }}>{m.email}</div>}
                </div>
                {m.email && (
                  <a href={`mailto:${m.email}`} className="no-tap-highlight" style={{ padding: "6px 10px", borderRadius: 10, background: COLORS.lilac, color: COLORS.purple, fontWeight: 800, fontSize: 12, textDecoration: "none" }}>Email</a>
                )}
              </div>
            ))}
          </>
        )}

        {b === "donations" && (
          <>
            <div style={{ color: COLORS.muted, fontWeight: 600, fontSize: 13, marginBottom: 12 }}>{donations.length} donation{donations.length === 1 ? "" : "s"}</div>
            {donations.map((d) => (
              <div key={d.id} className="ls-card" style={{ padding: 14, marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div className="font-heading" style={{ fontWeight: 800, fontSize: 18, color: COLORS.purple }}>R {d.amount}</div>
                    <div style={{ fontSize: 12, color: COLORS.muted, fontWeight: 700 }}>{d.category}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: d.status?.includes("completed") || d.status?.includes("success") ? "#2d8a4d" : COLORS.muted }}>{d.status}</div>
                    <div style={{ fontSize: 10.5, color: COLORS.muted, fontWeight: 600, marginTop: 2 }}>{new Date(d.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
              </div>
            ))}
            {donations.length === 0 && <div style={{ color: COLORS.muted, fontWeight: 600, padding: 20, textAlign: "center" }}>You haven't made any donations yet.</div>}
          </>
        )}

        {(b === "contact" || (isPhoneApp && ["directory", "team"].includes(b))) && contact && <ContactBlock contact={contact} />}

        {b === "community" && detail.section && (
          <>
            <div className="ls-card" style={{ padding: 0, overflow: "hidden", marginBottom: 16 }}>
              <div style={{ height: 140, background: `linear-gradient(135deg, ${COLORS.purple}, ${COLORS.plum})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Mark size={72} tone="white" />
              </div>
            </div>
            <div style={{ fontSize: 15, lineHeight: 1.65, fontWeight: 600, color: "#5a4d63" }}>{detail.section.body}</div>
            <button data-testid="detail-support-btn" onClick={() => { onClose(); setTimeout(() => onNav("give"), 100); }} className="ls-gold-btn" style={{ width: "100%", marginTop: 22, padding: 15 }}>
              Support this
            </button>
          </>
        )}

        {staticContent && !staticContent._loader && (
          <>
            <div className="ls-card" style={{ padding: 0, overflow: "hidden", marginBottom: 16 }}>
              <div style={{ height: 130, background: `linear-gradient(135deg, ${COLORS.purple}, ${COLORS.plum})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <img src="/assets/linkshul-logo.png" alt="LinkShul" style={{ height: 90, borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.3)" }} />
              </div>
            </div>
            {(isPhoneApp && b === "team" ? ["Rabbi Levi Avtzon and Chaya Avtzon lead our community with warmth, wisdom and love.", "**Office**\\nOur dedicated team runs day-to-day operations and supports members’ needs.", "**Committees**\\nVolunteer committees have guided the LinkShul community with responsibility and vision."] : staticContent.paragraphs).map((p, i) => {
              const text = isPhoneApp ? p.replace(/(?:the )?Linksfield[- ]Senderwood Hebrew (?:Community Synagogue|Congregation)/gi, "LinkShul") : p;
              const lines = text.split("\n");
              return (
                <div key={i} style={{ fontSize: 14.5, lineHeight: 1.65, fontWeight: 600, color: "#5a4d63", marginBottom: 12 }}>
                  {lines.map((l, j) => (
                    <p key={j} style={{ margin: "4px 0" }}
                      dangerouslySetInnerHTML={{ __html: l.replace(/\*\*(.+?)\*\*/g, `<strong style="color:${COLORS.purple}">$1</strong>`) }} />
                  ))}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

function ContactBlock({ contact }) {
  const Row = ({ label, value, href, testId }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #f0ebf4", gap: 12 }}>
      <span style={{ color: COLORS.muted, fontWeight: 700, fontSize: 13 }}>{label}</span>
      {href ? (
        <a href={href} data-testid={testId} style={{ fontWeight: 700, color: COLORS.purple, textDecoration: "none", textAlign: "right", wordBreak: "break-word" }}>{value}</a>
      ) : (
        <span style={{ fontWeight: 700, textAlign: "right", wordBreak: "break-word" }}>{value}</span>
      )}
    </div>
  );
  return (
    <>
      <div className="ls-card" style={{ padding: 18, marginBottom: 14 }}>
        <div className="font-heading" style={{ fontWeight: 800, marginBottom: 4, color: COLORS.purple, fontSize: 17 }}>{contact.office.title}</div>
        <div style={{ fontSize: 12, color: COLORS.muted, fontWeight: 600, marginBottom: 6 }}>{isPhoneApp ? "LinkShul" : "LinkShul"}</div>
        <Row label="Email" value={contact.office.email} href={`mailto:${contact.office.email}`} testId="contact-office-email" />
        <Row label="Phone" value={contact.office.phone} href={`tel:${contact.office.phone_intl}`} testId="contact-office-phone" />
      </div>
      <div className="ls-card" style={{ padding: 18, marginBottom: 14 }}>
        <div className="font-heading" style={{ fontWeight: 800, marginBottom: 8, color: COLORS.purple, fontSize: 17 }}>{contact.accounts.title}</div>
        <Row label="Email" value={contact.accounts.email} href={`mailto:${contact.accounts.email}`} testId="contact-accounts-email" />
        {contact.operations && <Row label="Operations" value={contact.operations.email} href={`mailto:${contact.operations.email}`} />}
        {contact.additional_email && <Row label="Rabbi Levi" value={contact.additional_email} href={`mailto:${contact.additional_email}`} />}
      </div>
      <div className="ls-card" style={{ padding: 18, marginBottom: 14 }}>
        <div className="font-heading" style={{ fontWeight: 800, marginBottom: 8, color: COLORS.purple, fontSize: 17 }}>{contact.rabbinic_couple.title}</div>
        {contact.rabbinic_couple.names.map((n) => <div key={n} style={{ padding: "6px 0", fontWeight: 700, fontSize: 15 }}>{n}</div>)}
        <div style={{ marginTop: 8, color: COLORS.muted, fontWeight: 600, fontSize: 13, fontStyle: "italic" }}>{contact.rabbinic_couple.note}</div>
      </div>
      <div className="ls-card" style={{ padding: 18, marginBottom: 14 }}>
        <div className="font-heading" style={{ fontWeight: 800, marginBottom: 8, color: COLORS.purple, fontSize: 17 }}>{contact.mikvah.title}</div>
        <Row label="Phone" value={contact.mikvah.phone} href={`tel:${contact.mikvah.phone_intl}`} testId="contact-mikvah-phone" />
      </div>
      {!isPhoneApp && <div className="ls-card" style={{ padding: 18, marginBottom: 14 }}>
        <div className="font-heading" style={{ fontWeight: 800, marginBottom: 8, color: COLORS.purple, fontSize: 17 }}>{contact.youth.title}</div>
        <Row label="Email" value={contact.youth.email} href={`mailto:${contact.youth.email}`} testId="contact-youth-email" />
      </div>}
      <div style={{ textAlign: "center", marginTop: 20 }}>
        <a href={contact.website} target="_blank" rel="noopener noreferrer" data-testid="contact-website-link" style={{ color: COLORS.purple, fontWeight: 800, textDecoration: "none", fontSize: 14 }}>
          Visit www.linkshul.co.za →
        </a>
      </div>
    </>
  );
}
